/* BuyerInvoiceDetailDialog — Phase 2c.
 *
 * Shared invoice detail dialog used by every buyer-facing invoice
 * surface (BuyerPayments open invoices block, BuyerOrders invoice rows,
 * etc.) so buyers see a consistent breakdown including the
 * shipping_passthrough line when present.
 *
 * Why this exists:
 *   When the seller books a carrier from /seller/shipping, the cost is
 *   auto-added to the buyer's invoice via shipping_passthrough. Without
 *   this dialog, buyers see a mysteriously inflated invoice total. Now
 *   they see "Items $15 + Shipping (USPS · TRK123) $12.41 = $27.41" with
 *   the carrier name, optional markup %, and tracking number visible.
 *
 * Also exposes the canonical PDF download from /api/invoices/{id}/pdf.
 */
import { useState } from "react";
import api, { money } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { Truck, Download, Loader2, Clock, AlertTriangle } from "lucide-react";
import InvoiceExpiryChip from "./InvoiceExpiryChip";
import CreditMemoTimeline from "./CreditMemoTimeline";

export default function BuyerInvoiceDetailDialog({ invoice, onClose }) {
  const [downloading, setDownloading] = useState(false);
  const open = !!invoice;
  if (!invoice) return null;

  const pt = invoice.shipping_passthrough || null;
  const billed = pt ? Number(pt.billed) || 0 : 0;
  const itemsSubtotal = Math.max(0, Number(invoice.amount || 0) - billed);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/invoices/${invoice.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${invoice.code || invoice.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't download PDF");
    } finally { setDownloading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="buyer-invoice-detail">
        <DialogHeader>
          <DialogTitle className="display text-xl tracking-tight">
            Invoice {invoice.code}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          {/* Meta */}
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[12.5px]">
            <span className="text-foreground/55">Status</span>
            <span className="text-right">
              <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider ${
                invoice.status === "paid" ? "bg-emerald-500/15 text-emerald-700"
                : invoice.status === "overdue" ? "bg-rose-500/15 text-rose-700"
                : invoice.status === "voided" ? "bg-foreground/10 text-foreground/50"
                : "bg-foreground/10 text-foreground/65"
              }`}>{invoice.status}</span>
            </span>
            <span className="text-foreground/55">Seller</span>
            <span className="text-right">{invoice.seller_name || "—"}</span>
            <span className="text-foreground/55">Terms</span>
            <span className="text-right uppercase">{(invoice.terms || "net30").replace("_", "-")}</span>
            <span className="text-foreground/55">Due</span>
            <span className="text-right">{invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : "—"}</span>
            {invoice.expires_at && (
              <>
                <span className="text-foreground/55 inline-flex items-center gap-1">
                  <Clock className="w-3 h-3"/>Validity
                </span>
                <span className="text-right">
                  <InvoiceExpiryChip invoice={invoice} compact/>
                </span>
              </>
            )}
          </div>

          {/* Phase IV — voided/expired banner with clear next action.
           *  Buyer sees this whenever they view an invoice that has been
           *  auto-voided by the validity sweeper. The seller will need to
           *  re-issue at current price (or buyer can place a fresh PO). */}
          {(invoice.status === "voided" || (invoice.expires_at && new Date(invoice.expires_at) < new Date() && ["draft","sent","outstanding"].includes(invoice.status))) && (
            <div className="rounded-lg border border-rose-300 bg-rose-50/60 px-3 py-2.5 text-[12px] text-rose-900 flex items-start gap-2" data-testid="invoice-expired-banner">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
              <div>
                <div className="font-medium mb-0.5">This invoice expired</div>
                <div className="leading-snug">
                  The seller's invoice-validity window passed before you paid or authorized.
                  Ask the seller to re-issue at the current price, or send a new PO from your Orders tab.
                </div>
              </div>
            </div>
          )}

          {/* Breakdown */}
          <div className="rounded-lg border border-border bg-secondary/30 p-3.5 space-y-1.5 text-[12.5px]" data-testid="invoice-breakdown">
            <div className="flex justify-between">
              <span className="text-foreground/65">Items subtotal</span>
              <span className="tabular-nums">{money(itemsSubtotal)}</span>
            </div>
            {pt && billed > 0 && (
              <div className="flex justify-between" data-testid="invoice-shipping-line">
                <span className="text-foreground/65 inline-flex items-center gap-1.5">
                  <Truck className="w-3 h-3"/>
                  Shipping
                  {pt.carrier && <span className="text-foreground/45">· {pt.carrier}</span>}
                </span>
                <span className="tabular-nums">{money(billed)}</span>
              </div>
            )}
            {pt && Number(pt.markup_pct || 0) > 0 && (
              <div className="text-[10.5px] text-foreground/45 pl-4 -mt-1">
                Carrier {money(pt.cost || 0)} + {pt.markup_pct}% handling
                {pt.tracking && <> · <span className="font-mono">{pt.tracking}</span></>}
              </div>
            )}
            <div className="border-t border-foreground/15 pt-1.5 mt-1.5 flex justify-between font-semibold">
              <span>Total due</span>
              <span className="tabular-nums">{money(invoice.amount || 0)}</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 justify-end pt-1">
            <Button variant="outline" size="sm" className="rounded-full"
                    onClick={downloadPdf} disabled={downloading}
                    data-testid="buyer-invoice-pdf">
              {downloading
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/>Generating…</>
                : <><Download className="w-3.5 h-3.5 mr-1.5"/>Download PDF</>}
            </Button>
            <Button size="sm" className="rounded-full bg-foreground text-background" onClick={onClose}>
              Close
            </Button>
          </div>

          {/* Credit memos issued against this invoice (refunds → buyer wallet) */}
          <div className="pt-2">
            <CreditMemoTimeline invoiceId={invoice.id}/>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
